/**
 * The measurements form's fields, their bounds and the cm/in conversion.
 *
 * Stored in centimetres whatever the shopper types in: the size charts are in centimetres, and a
 * value kept in the unit it was typed in would need its unit stored beside it forever.
 */

export type MeasurementKey = 'heightCm' | 'chestCm' | 'underBustCm' | 'waistCm' | 'hipsCm'
export type MeasurementUnit = 'cm' | 'in'

export const CM_PER_INCH = 2.54

export interface MeasurementField {
  key: MeasurementKey
  labelKey: string
  /** How to measure it — the size chart sheet's own instructions. */
  hintKey: string
}

// Labels of their own, short enough to head a column of large figures ("Pod prsy"): the size
// chart's are worded for a table header, and some of them are mistranslated.
export const MEASUREMENT_FIELDS: readonly MeasurementField[] = [
  { key: 'heightCm', labelKey: 'account.sizes.measure.height', hintKey: 'model.profile.guide_height' },
  { key: 'chestCm', labelKey: 'account.sizes.measure.chest', hintKey: 'product.size_chart_sheet.chest_description' },
  { key: 'underBustCm', labelKey: 'account.sizes.measure.under_bust', hintKey: 'product.size_chart_sheet.underbust_description' },
  { key: 'waistCm', labelKey: 'account.sizes.measure.waist', hintKey: 'product.size_chart_sheet.waist_description' },
  { key: 'hipsCm', labelKey: 'account.sizes.measure.hips', hintKey: 'product.size_chart_sheet.hips_description' },
]

/**
 * Plausible adult ranges in centimetres. Mirrors `MEASUREMENT_BOUNDS` in vendure-app's
 * customer-size-profile plugin, which refuses anything outside them; checked here first so the
 * shopper hears about a slip next to the field instead of from a failed save.
 */
export const MEASUREMENT_BOUNDS: Record<MeasurementKey, { min: number, max: number }> = {
  heightCm: { min: 100, max: 230 },
  chestCm: { min: 50, max: 200 },
  underBustCm: { min: 45, max: 180 },
  waistCm: { min: 40, max: 200 },
  hipsCm: { min: 50, max: 200 },
}

/** One decimal at most, and none when the value is whole: "92", "36.2". */
function round(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * A value in the unit on screen, to one decimal at most. Given a formatter, in the page language's
 * own way ("92,5" in Czech); `parseToCm` reads either decimal separator back.
 */
export function toDisplayValue(cm: number | null | undefined, unit: MeasurementUnit, format?: Intl.NumberFormat): string {
  if (cm === null || cm === undefined)
    return ''
  const value = round(unit === 'in' ? cm / CM_PER_INCH : cm)
  return format ? format.format(value) : String(value)
}

/**
 * What the shopper typed, in centimetres: null for an empty field, NaN for anything that is not a
 * number. A decimal comma is accepted — it is how most of the markets write one.
 */
export function parseToCm(text: string, unit: MeasurementUnit): number | null {
  const trimmed = text.trim().replace(',', '.')
  if (!trimmed)
    return null
  const value = Number(trimmed)
  if (!Number.isFinite(value))
    return Number.NaN
  return unit === 'in' ? value * CM_PER_INCH : value
}

export function isWithinBounds(key: MeasurementKey, cm: number): boolean {
  const { min, max } = MEASUREMENT_BOUNDS[key]
  return cm >= min && cm <= max
}

/** A field's bounds in the unit on screen, rounded outwards so the message never excludes a valid value. */
export function displayBounds(key: MeasurementKey, unit: MeasurementUnit): { min: number, max: number } {
  const { min, max } = MEASUREMENT_BOUNDS[key]
  if (unit === 'cm')
    return { min, max }
  return { min: Math.ceil(min / CM_PER_INCH), max: Math.floor(max / CM_PER_INCH) }
}
